import { ApiHandler } from '../api-handler';
import { ApiEndpoints, HttpMethod } from '../constants';
import { GeneralApiResponse } from '../types';
import {
  CustomerHistoryApiResponse,
  CustomerHistoryParams,
  CountriesApiResponse,
  CountryApiResponse,
  CreateCountryPayload,
  UpdateCountryPayload,
  PlatformStatesApiResponse,
  PlatformStateApiResponse,
  CreateStatePayload,
  UpdateStatePayload,
  PmsListApiResponse,
  PmsApiResponse,
  CreatePmsPayload,
  UpdatePmsPayload,
  SpecialtyTreesApiResponse,
  SpecialtyTreeApiResponse,
  SpecialtyLinkImpactApiResponse,
  SpecialtyDeletePreviewApiResponse,
  SpecialtyDeleteApiResponse,
  DeleteSpecialtyPayload,
  IntakeRepairPreviewApiResponse,
  IntakeRepairApiResponse,
  IntakeRepairMode,
  LatestIntakeRepair,
  CreateSpecialtyPayload,
  UpdateSpecialtyPayload,
  CppVersionsApiResponse,
  CppVersionApiResponse,
  CppDeletePreviewApiResponse,
  CppDeleteApiResponse,
  DeleteCppPayload,
  CreateCppVersionPayload,
  UpdateCppVersionContentPayload,
  UpdateCppVersionStatusPayload,
  DriveListApiResponse,
  ImportFromDrivePayload,
  ClinicsApiResponse,
  ClinicApiResponse,
  OnboardClinicPayload,
  UpdateClinicPayload,
  ClinicUsersApiResponse,
  ClinicUserApiResponse,
  AddClinicUserPayload,
  UpdateClinicUserPayload,
  ClinicUserAnalyticsApiResponse,
  StripeProductsApiResponse,
  StripeProductUpdatePayload,
  ClinicSubscriptionApiResponse,
  ClinicInvoicesApiResponse,
  StripeCustomerApiResponse,
  CheckoutSessionApiResponse,
  PlatformUsersApiResponse,
  PlatformUserApiResponse,
  InvitePlatformUserPayload,
  UpdatePlatformUserPayload,
  StripePlanMirrorApiResponse,
  DoctorReconciliationApiResponse,
  CatalogueResourceKind,
  CatalogueDeletePreviewApiResponse,
  CatalogueDeleteApiResponse,
  DeleteCataloguePayload,
} from './types';

export interface DashboardStats {
  totalClinics: number;
  activeDoctors: number;
  specialties: number;
  pendingInvitations: number;
}

export type DashboardStatsApiResponse = GeneralApiResponse<DashboardStats>;

// ── Engagement analytics (spec 2026-07-25-admin-portal-analytics) ─────

export interface PlatformFunnel {
  landed: number;
  started: number;
  connected: number;
  completed: number;
  landedToStartedPct: number | null;
  startedToCompletedPct: number | null;
}

export interface DemographicCell {
  ageBand: string;
  sex: string | null;
  n: number | null;
  suppressed: boolean;
}

export interface PlatformAnalytics {
  textEngagementPercentage: number;
  voiceVsTextPercentage: { voice: number; text: number };
  completionPercentage: number;
  /** Null when no Session qualified — never 0, which would read as "instant". */
  averageSessionDurationSeconds: number | null;
  averageSessionDurationFormatted: string | null;
  /** Sessions behind the mean; below the completed count before 2026-07-25. */
  averageSessionDurationSampleSize: number;
  totalSessions: number;
  totalUsers: number;
  abandonedSessions: number;
  funnel: PlatformFunnel;
  modeSplit: Array<{ mode: string; n: number }>;
  imageStats: {
    offered: number;
    sessionsWithUpload: number;
    totalUploads: number;
    pctOfStartedSessions: number | null;
  };
  dropout: {
    byReason: Array<{ reason: string; n: number }>;
    byLastNode: Array<{
      node: string;
      n: number;
      /** null when no session at this node has a recorded dwell time (legacy data). */
      avgDwellSeconds: number | null;
      /** Dwell < 5s before abandoning — suggests refusal of that question. */
      instantCount: number;
      /** Dwell >= 60s before abandoning — suggests confusion/stuck. */
      longDwellCount: number;
    }>;
  };
  demographics: DemographicCell[];
}

export interface DoctorBreakdownRow {
  doctorId: string;
  doctorName: string;
  clinicName: string | null;
  sessions: number;
  completionRate: number | null;
  funnelConversion: number | null;
  voicePct: number | null;
  textPct: number | null;
  avgDurationSecs: number | null;
}

export interface PlatformAnalyticsData {
  platform: PlatformAnalytics;
  byDoctor: DoctorBreakdownRow[];
}

export type PlatformAnalyticsApiResponse =
  GeneralApiResponse<PlatformAnalyticsData>;

// ── Dashboard analytics (ADR 0002 — window-ratio semantics) ────────────
export interface TrendPoint {
  date: string;
  pct: number | null;
  numerator: number;
  denominator: number;
}

export interface SpecialtyRollup {
  specialtyId: string | null;
  name: string;
  activePhysicians: number;
  landings: number;
  started: number;
  completed: number;
  startedPctOfLandings: number | null;
  completedPctOfStarted: number | null;
  voicePct: number | null;
  imagePct: number | null;
}

export interface PhysicianRow {
  doctorId: string;
  doctorName: string;
  specialtyName: string;
  landings: number;
  started: number;
  completed: number;
  completionPct: number | null;
  /** Mean consult seconds in window; null when no session qualified. */
  avgDurationSecs: number | null;
}

export interface DashboardAnalytics {
  meta: {
    windowDays: 7 | 30;
    from: string;
    to: string;
    releaseFloor: string;
    /** True when the window start was clamped to the release floor. */
    clamped: boolean;
    provisionalCompletion: true;
  };
  cards: {
    landings: { n: number; deltaVsPriorPct: number | null };
    started: { n: number; pctOfLandings: number | null };
    completed: { n: number; pctOfStarted: number | null };
    /**
     * Mean seconds from patient connect to session end, completed sessions
     * only (#297). `n` is how many sessions qualified — below `completed.n`
     * when a connect event is missing.
     */
    avgDuration: {
      secs: number | null;
      n: number;
      deltaVsPriorPct: number | null;
    };
  };
  completionTrend: TrendPoint[];
  engagement: {
    voicePct: number | null;
    textPct: number | null;
    modeSwitchedPct: number | null;
    imageSessionsPct: number | null;
    sessionsWithUpload: number;
    totalUploads: number;
    offered: number;
  };
  demographics: DemographicCell[];
  dropout: PlatformAnalytics['dropout'];
  adoption: {
    activePhysicians: number;
    landingsPerActivePhysician: number | null;
    weightedCompletionPct: number | null;
    bySpecialty: SpecialtyRollup[];
    byPhysician: PhysicianRow[];
  };
  specialtyOptions: Array<{ _id: string; name: string }>;
}

export type DashboardAnalyticsApiResponse =
  GeneralApiResponse<DashboardAnalytics>;

export type DoctorDashboardAnalytics = Omit<
  DashboardAnalytics,
  'adoption' | 'specialtyOptions'
>;

export interface DashboardAnalyticsParams {
  window?: 7 | 30;
  specialty?: string; // Specialty _id or 'unknown'
}

export const PlatformApiService = {
  getCustomerHistory: (params: CustomerHistoryParams) =>
    ApiHandler.makeApiRequest<CustomerHistoryApiResponse>({
      endpoint: ApiEndpoints.Platform.CustomerHistory,
      method: HttpMethod.Get,
      query: {
        kind: params.kind,
        recordState: params.recordState,
        subscription: params.subscription,
        includeExcluded: String(params.includeExcluded),
        page: String(params.page),
        pageSize: String(params.pageSize),
        search: params.search,
      },
    }),

  getCatalogueDeleteImpact: (kind: CatalogueResourceKind, id: string) =>
    ApiHandler.makeApiRequest<CatalogueDeletePreviewApiResponse>({
      endpoint: `/platform/catalogue/${kind}/${id}/delete-impact`,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  deleteCatalogueRecord: (kind: CatalogueResourceKind, id: string, data: DeleteCataloguePayload) =>
    ApiHandler.makeApiRequest<CatalogueDeleteApiResponse, DeleteCataloguePayload>({
      endpoint: `/platform/catalogue/${kind}/${id}`,
      method: HttpMethod.Delete,
      rejectOnError: true,
      data,
    }),

  // ── Dashboard ──────────────────────────────────────────────────────
  getDashboardStats: () =>
    ApiHandler.makeApiRequest<DashboardStatsApiResponse>({
      endpoint: '/platform/dashboard/stats',
      method: HttpMethod.Get,
    }),

  // ── Engagement analytics ───────────────────────────────────────────
  getAnalytics: () =>
    ApiHandler.makeApiRequest<PlatformAnalyticsApiResponse>({
      endpoint: '/platform/analytics',
      method: HttpMethod.Get,
    }),

  getDashboardAnalytics: (params: DashboardAnalyticsParams = {}) => {
    const qs = new URLSearchParams();
    if (params.window) qs.set('window', String(params.window));
    if (params.specialty) qs.set('specialty', params.specialty);
    const query = qs.toString();
    return ApiHandler.makeApiRequest<DashboardAnalyticsApiResponse>({
      endpoint: `/platform/analytics/dashboard${query ? `?${query}` : ''}`,
      method: HttpMethod.Get,
    });
  },

  // ── Countries ──────────────────────────────────────────────────────
  getCountries: () =>
    ApiHandler.makeApiRequest<CountriesApiResponse>({
      endpoint: ApiEndpoints.Platform.Countries,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  createCountry: (data: CreateCountryPayload) =>
    ApiHandler.makeApiRequest<CountryApiResponse, CreateCountryPayload>({
      endpoint: ApiEndpoints.Platform.Countries,
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  updateCountry: (id: string, data: UpdateCountryPayload) =>
    ApiHandler.makeApiRequest<CountryApiResponse, UpdateCountryPayload>({
      endpoint: ApiEndpoints.Platform.CountryById(id),
      method: HttpMethod.Put,
      rejectOnError: true,
      data,
    }),

  deleteCountry: (id: string) =>
    ApiHandler.makeApiRequest<GeneralApiResponse>({
      endpoint: ApiEndpoints.Platform.CountryById(id),
      method: HttpMethod.Delete,
      rejectOnError: true,
    }),

  // ── States ─────────────────────────────────────────────────────────
  getStates: () =>
    ApiHandler.makeApiRequest<PlatformStatesApiResponse>({
      endpoint: ApiEndpoints.Platform.States,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  createState: (data: CreateStatePayload) =>
    ApiHandler.makeApiRequest<PlatformStateApiResponse, CreateStatePayload>({
      endpoint: ApiEndpoints.Platform.States,
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  updateState: (id: string, data: UpdateStatePayload) =>
    ApiHandler.makeApiRequest<PlatformStateApiResponse, UpdateStatePayload>({
      endpoint: ApiEndpoints.Platform.StateById(id),
      method: HttpMethod.Put,
      rejectOnError: true,
      data,
    }),

  deleteState: (id: string) =>
    ApiHandler.makeApiRequest<GeneralApiResponse>({
      endpoint: ApiEndpoints.Platform.StateById(id),
      method: HttpMethod.Delete,
      rejectOnError: true,
    }),

  // ── PMS ────────────────────────────────────────────────────────────
  getPmsList: () =>
    ApiHandler.makeApiRequest<PmsListApiResponse>({
      endpoint: ApiEndpoints.Platform.Pms,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  createPms: (data: CreatePmsPayload) =>
    ApiHandler.makeApiRequest<PmsApiResponse, CreatePmsPayload>({
      endpoint: ApiEndpoints.Platform.Pms,
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  updatePms: (id: string, data: UpdatePmsPayload) =>
    ApiHandler.makeApiRequest<PmsApiResponse, UpdatePmsPayload>({
      endpoint: ApiEndpoints.Platform.PmsById(id),
      method: HttpMethod.Put,
      rejectOnError: true,
      data,
    }),

  deletePms: (id: string) =>
    ApiHandler.makeApiRequest<GeneralApiResponse>({
      endpoint: ApiEndpoints.Platform.PmsById(id),
      method: HttpMethod.Delete,
      rejectOnError: true,
    }),

  // ── Specialties ────────────────────────────────────────────────────
  getSpecialties: () =>
    ApiHandler.makeApiRequest<SpecialtyTreesApiResponse>({
      endpoint: ApiEndpoints.Platform.Specialties,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  createSpecialty: (data: CreateSpecialtyPayload) =>
    ApiHandler.makeApiRequest<SpecialtyTreeApiResponse, CreateSpecialtyPayload>(
      {
        endpoint: ApiEndpoints.Platform.Specialties,
        method: HttpMethod.Post,
        rejectOnError: true,
        data,
      },
    ),

  updateSpecialty: (id: string, data: UpdateSpecialtyPayload) =>
    ApiHandler.makeApiRequest<SpecialtyTreeApiResponse, UpdateSpecialtyPayload>(
      {
        endpoint: ApiEndpoints.Platform.SpecialtyById(id),
        method: HttpMethod.Put,
        rejectOnError: true,
        data,
      },
    ),

  /** Blast radius of a slug change, for the pre-save warning (ADR 0003 D10). */
  getSpecialtyLinkImpact: (id: string) =>
    ApiHandler.makeApiRequest<SpecialtyLinkImpactApiResponse>({
      endpoint: ApiEndpoints.Platform.SpecialtyLinkImpact(id),
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  previewIntakeRepair: (id: string, data: { mode: IntakeRepairMode }) =>
    ApiHandler.makeApiRequest<IntakeRepairPreviewApiResponse, { mode: IntakeRepairMode }>({
      endpoint: `/platform/specialties/${id}/intake-repair/preview`,
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  getLatestIntakeRepair: () =>
    ApiHandler.makeApiRequest<GeneralApiResponse<LatestIntakeRepair | null>>({
      endpoint: '/platform/specialties/intake-repair/latest',
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  applyIntakeRepair: (
    id: string,
    data: { mode: IntakeRepairMode; expectedFingerprint: string; resetDoctorReferences: boolean },
  ) =>
    ApiHandler.makeApiRequest<
      IntakeRepairApiResponse,
      { mode: IntakeRepairMode; expectedFingerprint: string; resetDoctorReferences: boolean }
    >({
      endpoint: `/platform/specialties/${id}/intake-repair/apply`,
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  undoIntakeRepair: (
    id: string,
    data: { auditId: string; expectedFingerprint: string },
  ) =>
    ApiHandler.makeApiRequest<
      GeneralApiResponse<{ restored: true }>,
      { auditId: string; expectedFingerprint: string }
    >({
      endpoint: `/platform/specialties/${id}/intake-repair/undo`,
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  getSpecialtyDeleteImpact: (id: string) =>
    ApiHandler.makeApiRequest<SpecialtyDeletePreviewApiResponse>({
      endpoint: `/platform/specialties/${id}/delete-impact`,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  deleteSpecialty: (id: string, data: DeleteSpecialtyPayload) =>
    ApiHandler.makeApiRequest<SpecialtyDeleteApiResponse, DeleteSpecialtyPayload>({
      endpoint: ApiEndpoints.Platform.SpecialtyById(id),
      method: HttpMethod.Delete,
      rejectOnError: true,
      data,
    }),

  // ── CPP Versions ────────────────────────────────────────────────────
  getCppVersions: (nodeId: string) =>
    ApiHandler.makeApiRequest<CppVersionsApiResponse>({
      endpoint: ApiEndpoints.Platform.CppByNode(nodeId),
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  getPublishedCpp: (nodeId: string) =>
    ApiHandler.makeApiRequest<CppVersionApiResponse>({
      endpoint: ApiEndpoints.Platform.CppPublished(nodeId),
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  createCppVersion: (data: CreateCppVersionPayload) =>
    ApiHandler.makeApiRequest<CppVersionApiResponse, CreateCppVersionPayload>({
      endpoint: ApiEndpoints.Platform.Cpp,
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  updateCppVersionContent: (id: string, data: UpdateCppVersionContentPayload) =>
    ApiHandler.makeApiRequest<
      CppVersionApiResponse,
      UpdateCppVersionContentPayload
    >({
      endpoint: ApiEndpoints.Platform.CppById(id),
      method: HttpMethod.Put,
      rejectOnError: true,
      data,
    }),

  updateCppVersionStatus: (id: string, data: UpdateCppVersionStatusPayload) =>
    ApiHandler.makeApiRequest<
      CppVersionApiResponse,
      UpdateCppVersionStatusPayload
    >({
      endpoint: ApiEndpoints.Platform.CppStatusById(id),
      method: HttpMethod.Put,
      rejectOnError: true,
      data,
    }),

  getCppVersionDeleteImpact: (id: string) =>
    ApiHandler.makeApiRequest<CppDeletePreviewApiResponse>({
      endpoint: `/platform/cpp/${id}/delete-impact`,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  getCppVersionsDeleteImpact: (nodeId: string) =>
    ApiHandler.makeApiRequest<CppDeletePreviewApiResponse>({
      endpoint: `/platform/cpp/node/${nodeId}/delete-impact`,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  deleteCppVersion: (id: string, data: DeleteCppPayload) =>
    ApiHandler.makeApiRequest<CppDeleteApiResponse, DeleteCppPayload>({
      endpoint: ApiEndpoints.Platform.CppById(id),
      method: HttpMethod.Delete,
      rejectOnError: true,
      data,
    }),

  deleteCppVersionsForSpecialty: (nodeId: string, data: DeleteCppPayload) =>
    ApiHandler.makeApiRequest<CppDeleteApiResponse, DeleteCppPayload>({
      endpoint: ApiEndpoints.Platform.CppVersionsByNodePermanently(nodeId),
      method: HttpMethod.Delete,
      rejectOnError: true,
      data,
    }),

  // ── Drive Integration ───────────────────────────────────────────────
  getDriveConfig: () =>
    ApiHandler.makeApiRequest<
      GeneralApiResponse<{ rootFolderId: string | null }>
    >({
      endpoint: ApiEndpoints.Platform.DriveConfig,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  listDriveFolders: (folderId?: string) =>
    ApiHandler.makeApiRequest<DriveListApiResponse>({
      endpoint: ApiEndpoints.Platform.DriveFolders,
      method: HttpMethod.Get,
      rejectOnError: true,
      ...(folderId ? { query: { folderId } } : {}),
    }),

  importFromDrive: (data: ImportFromDrivePayload) =>
    ApiHandler.makeApiRequest<CppVersionApiResponse, ImportFromDrivePayload>({
      endpoint: ApiEndpoints.Platform.DriveImport,
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  // ── Clinics ─────────────────────────────────────────────────────────
  getClinics: (search?: string, includeDeleted = false) =>
    ApiHandler.makeApiRequest<ClinicsApiResponse>({
      endpoint: ApiEndpoints.Platform.Clinics,
      method: HttpMethod.Get,
      rejectOnError: true,
      ...((search || includeDeleted) ? { query: { ...(search ? { search } : {}), ...(includeDeleted ? { includeDeleted: 'true' } : {}) } } : {}),
    }),

  getClinicDetail: (id: string) =>
    ApiHandler.makeApiRequest<ClinicApiResponse>({
      endpoint: ApiEndpoints.Platform.ClinicById(id),
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  onboardClinic: (data: OnboardClinicPayload) =>
    ApiHandler.makeApiRequest<ClinicApiResponse, OnboardClinicPayload>({
      endpoint: ApiEndpoints.Platform.Clinics,
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  updateClinic: (id: string, data: UpdateClinicPayload) =>
    ApiHandler.makeApiRequest<ClinicApiResponse, UpdateClinicPayload>({
      endpoint: ApiEndpoints.Platform.ClinicById(id),
      method: HttpMethod.Put,
      rejectOnError: true,
      data,
    }),

  activateClinic: (id: string) =>
    ApiHandler.makeApiRequest<ClinicApiResponse>({
      endpoint: ApiEndpoints.Platform.ClinicActivate(id),
      method: HttpMethod.Post,
      rejectOnError: true,
    }),

  deactivateClinic: (id: string) =>
    ApiHandler.makeApiRequest<ClinicApiResponse>({
      endpoint: ApiEndpoints.Platform.ClinicDeactivate(id),
      method: HttpMethod.Post,
      rejectOnError: true,
    }),

  getClinicUsers: (id: string) =>
    ApiHandler.makeApiRequest<ClinicUsersApiResponse>({
      endpoint: ApiEndpoints.Platform.ClinicUsers(id),
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  addClinicUser: (clinicId: string, data: AddClinicUserPayload) =>
    ApiHandler.makeApiRequest<ClinicUserApiResponse, AddClinicUserPayload>({
      endpoint: ApiEndpoints.Platform.ClinicUsers(clinicId),
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  updateClinicUser: (
    clinicId: string,
    userId: string,
    data: UpdateClinicUserPayload,
  ) =>
    ApiHandler.makeApiRequest<
      ClinicUserAnalyticsApiResponse,
      UpdateClinicUserPayload
    >({
      endpoint: ApiEndpoints.Platform.ClinicUserById(clinicId, userId),
      method: HttpMethod.Patch,
      rejectOnError: true,
      data,
    }),

  // ── Stripe ──────────────────────────────────────────────────────────
  getStripeProducts: () =>
    ApiHandler.makeApiRequest<StripeProductsApiResponse>({
      endpoint: ApiEndpoints.Platform.StripeProducts,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  syncStripeProducts: () =>
    ApiHandler.makeApiRequest<GeneralApiResponse>({
      endpoint: ApiEndpoints.Platform.StripeSync,
      method: HttpMethod.Post,
      rejectOnError: true,
    }),

  getStripeRateCard: () =>
    ApiHandler.makeApiRequest<StripePlanMirrorApiResponse>({
      endpoint: ApiEndpoints.Platform.BillingRateCard,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  getDoctorReconciliation: (
    doctorId: string,
    periodStart: string,
    periodEnd: string,
  ) =>
    ApiHandler.makeApiRequest<DoctorReconciliationApiResponse>({
      endpoint: `${ApiEndpoints.Platform.DoctorReconciliation(doctorId)}?periodStart=${encodeURIComponent(periodStart)}&periodEnd=${encodeURIComponent(periodEnd)}`,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  updateStripeProduct: (
    productId: string,
    payload: StripeProductUpdatePayload,
  ) =>
    ApiHandler.makeApiRequest<GeneralApiResponse, StripeProductUpdatePayload>({
      endpoint: ApiEndpoints.Platform.StripeProductById(productId),
      method: HttpMethod.Patch,
      data: payload,
      rejectOnError: true,
    }),

  getClinicSubscription: (clinicId: string) =>
    ApiHandler.makeApiRequest<ClinicSubscriptionApiResponse>({
      endpoint: ApiEndpoints.Platform.ClinicSubscription(clinicId),
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  getClinicInvoices: (clinicId: string) =>
    ApiHandler.makeApiRequest<ClinicInvoicesApiResponse>({
      endpoint: ApiEndpoints.Platform.ClinicInvoices(clinicId),
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  createClinicStripeCustomer: (clinicId: string) =>
    ApiHandler.makeApiRequest<StripeCustomerApiResponse>({
      endpoint: ApiEndpoints.Platform.ClinicStripeCustomer(clinicId),
      method: HttpMethod.Post,
      rejectOnError: true,
    }),

  createClinicCheckoutSession: (clinicId: string, priceId: string) =>
    ApiHandler.makeApiRequest<CheckoutSessionApiResponse, { priceId: string }>({
      endpoint: ApiEndpoints.Platform.ClinicCheckoutSession(clinicId),
      method: HttpMethod.Post,
      rejectOnError: true,
      data: { priceId },
    }),

  cancelClinicSubscription: (clinicId: string) =>
    ApiHandler.makeApiRequest<GeneralApiResponse>({
      endpoint: ApiEndpoints.Platform.ClinicCancelSubscription(clinicId),
      method: HttpMethod.Post,
      rejectOnError: true,
    }),

  refundClinicPayment: (
    clinicId: string,
    data: { type: 'full' | 'prorata' | 'custom'; amount?: number },
  ) =>
    ApiHandler.makeApiRequest<
      GeneralApiResponse,
      { type: string; amount?: number }
    >({
      endpoint: ApiEndpoints.Platform.ClinicRefund(clinicId),
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  // ── Platform Users ──────────────────────────────────────────────────
  getPlatformUsers: () =>
    ApiHandler.makeApiRequest<PlatformUsersApiResponse>({
      endpoint: ApiEndpoints.Platform.PlatformUsers,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  invitePlatformUser: (data: InvitePlatformUserPayload) =>
    ApiHandler.makeApiRequest<
      PlatformUserApiResponse,
      InvitePlatformUserPayload
    >({
      endpoint: ApiEndpoints.Platform.PlatformUsers,
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  updatePlatformUser: (id: string, data: UpdatePlatformUserPayload) =>
    ApiHandler.makeApiRequest<
      PlatformUserApiResponse,
      UpdatePlatformUserPayload
    >({
      endpoint: ApiEndpoints.Platform.PlatformUserById(id),
      method: HttpMethod.Put,
      rejectOnError: true,
      data,
    }),

  deletePlatformUser: (id: string) =>
    ApiHandler.makeApiRequest<GeneralApiResponse>({
      endpoint: ApiEndpoints.Platform.PlatformUserById(id),
      method: HttpMethod.Delete,
      rejectOnError: true,
    }),

  resendPlatformInvite: (id: string) =>
    ApiHandler.makeApiRequest<GeneralApiResponse>({
      endpoint: ApiEndpoints.Platform.PlatformUserResendInvite(id),
      method: HttpMethod.Post,
      rejectOnError: true,
    }),
} as const;
